import { translate } from "utils";
import {
	UploadOutlined,
	DeleteOutlined,
	FileOutlined,
} from "@ant-design/icons";
import { Button, message, Spin, Upload } from "antd";
import { useEffect, useState } from "react";
import { putFileS3 } from "service/uploadService";
import CheckAccess from "hoc/CheckAccess";
import { useSelector } from "react-redux";
import { selectMyProfile } from "store/auth/authSlice";
import { Empty } from "antd";
import { debounce } from "utils";
import moment from "moment";
import { FileItem } from "features/subscription-transaction/type";
import { PERMISSION } from "constants/permission";

interface Props {
	files: FileItem[];
	filesChange: (files: FileItem[]) => void;
	isHideLabel?: boolean;
	isShowError?: boolean;
}

const fileSizeLimit = 10 * 1024 * 1024; // size limit

const debounce400 = debounce(400);

export default function Files(props: Props) {
	const { files, filesChange, isHideLabel, isShowError } = props;
	const myProfile = useSelector(selectMyProfile);

	const [loading, setLoading] = useState(false);

	const handleChangeFile = async (info: any) => {
		debounce400(async () => {
			try {
				setLoading(true);
				if (!info.fileList.length) return;
				const newFiles: FileItem[] = [...files];
				for (let i = 0; i < info.fileList.length; i++) {
					const file = info.fileList[i];
					if (file.size > fileSizeLimit) {
						message.error(
							translate(
								"File size exceeds the limit. Please select a file smaller than 10MB",
							),
						);
						continue;
					}
					const { url } = (await putFileS3(file.originFileObj as File)) as any;

					newFiles.push({
						path: url,
						name: file.name,
						createdAt: moment().format("YYYY-MM-DD"),
						updatedBy: {
							id: myProfile.id,
							fullName: myProfile.fullName || "",
						},
					});
					filesChange(newFiles);
				}
			} finally {
				setLoading(false);
			}
		});
	};

	function handleDeleteFile(index: number) {
		window.dialogConfirm(
			translate("Confirm delete"),
			translate("Are you sure to delete it?"),
			(isConfirm: boolean) => {
				if (isConfirm) {
					const _files = [...files];
					_files.splice(index, 1);
					filesChange(_files);
				}
			},
			true,
		);
	}

	return (
		<Spin spinning={loading}>
			<div className={`mt-4 bg-white  ${!isHideLabel && "shadow-lg p-4"}`}>
				<p className="font-semibold text-lg ">{translate("File")} </p>

				<div className="mt-2 overflow-x-auto">
					<table className="min-w-[500px] w-full border">
						<thead>
							<tr className="bg-blue-400 text-white">
								<th className="border font-semibold p-2 w-[100px]">
									{translate("NO")}
								</th>
								<th className="border font-semibold p-2">
									{translate("Name")}
								</th>
								<th className="border font-semibold w-3/12 p-2">
									{translate("Payment date")}
								</th>
								<th className="border font-semibold w-2/12 p-2 text-center">
									{translate("Action")}
								</th>
							</tr>
						</thead>
						<tbody>
							{files.length ? (
								files.map((item, idx) => (
									<tr key={idx}>
										<td className="border p-2 text-center w-[100px]">
											{idx + 1}
										</td>
										<td className="border p-2 text-center">
											<a
												href={item.path}
												target="_blank"
												className="flex items-center gap-1"
											>
												<FileOutlined />
												{item.name}
											</a>
										</td>
										<td className="border p-2 text-center">
											{moment(item.createdAt).format("DD/MM/YYYY")}
										</td>
										<td className="border p-2 text-center">
											<DeleteOutlined
												className="cursor-pointer text-lg hover:text-red-600 duration-300"
												onClick={() => {
													handleDeleteFile(idx);
												}}
											/>
										</td>
									</tr>
								))
							) : (
								<tr>
									<td colSpan={6} className="no-data">
										<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<div className="mt-4">
					{isShowError && (
						<div className="font-normal text-sm text-red-500 mb-4">
							{translate("This field is required")}
						</div>
					)}
					<Upload
						beforeUpload={() => false}
						multiple
						fileList={[]}
						listType="picture"
						accept=".pdf, .doc, .docx, .xlsx, .png, .jpg"
						onChange={handleChangeFile}
					>
						<CheckAccess perms={[PERMISSION.TRANSACTIONS.UPDATE]}>
							<Button icon={<UploadOutlined />} className="flex items-center">
								{translate("UPLOAD")}
							</Button>
						</CheckAccess>
					</Upload>
				</div>
			</div>
		</Spin>
	);
}
